import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {useBuildCancelToken} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelToken,
} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeaderProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {AsyncLoader} from '../../loaders/asyncLoader';
import {DropdownEmpty} from '../dropdownEmpty';
import {DropdownLoader} from '../dropdownLoader';
import {SearchDropdown} from './searchDropdown';
import {SearchDropdownHeaderOptionalProps} from './searchDropdownHeader';
import {SearchDropdownCommonProps} from './searchDropdownTypes';

/*
 * Constants.
 */

const defaultGracePeriod = 300;

/*
 * Style.
 */

const StyledLoaderDiv = styled.div`
  height: 100%;
  ${center()};
`;

/*
 * Types.
 */

interface AsyncSearchDropdownResponse<T> {
  data: ReadonlyArray<T>;
  canRequestMore: boolean;
}

type AsyncSearchDropdownDataFetcher<T> = (
  query: string,
  data: ReadonlyArray<T>,
  cancelToken: CancelToken,
) => Promise<AsyncSearchDropdownResponse<T>>;

/*
 * Props.
 */

interface AsyncSearchDropdownResults<T> extends AsyncSearchDropdownResponse<T> {
  query: string;
}

export interface AsyncSearchDropdownProps<T>
  extends DropdownProps,
    DropdownHeaderProps,
    SearchDropdownHeaderOptionalProps,
    SearchDropdownCommonProps<T> {
  /*
   * Fetching function to get both initial data, and more data when the user scrolls to the
   * bottom of the search dropdown.
   *
   * query: The query the user typed into the box
   * data: The current data being displayed to the user
   *
   * The result of this function should be a promise that resolves with the total list of
   * data that should be displayed. In the load more case, it should be concatenated onto
   * the data array provided as a parameter.
   */
  onRequestData: AsyncSearchDropdownDataFetcher<T>;

  /* The time in ms before a loading spinner shows. */
  gracePeriod?: number;

  /* Custom view to show when loading data. */
  renderLoading?: Renderer;

  /* Custom view to show when no results are found for a query. */
  renderEmpty?: Renderer;
}

export const AsyncSearchDropdown = <T,>(props: AsyncSearchDropdownProps<T>) => {
  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);

  const renderDefaultEmpty = () => <DropdownEmpty />;
  const renderDefaultLoading = () => (
    <DropdownLoader status={status} onRetryClick={() => fetchData(query, [])} />
  );

  const {renderEmpty = renderDefaultEmpty, renderLoading = renderDefaultLoading} = props;

  const [response, setResponse] = useState<AsyncSearchDropdownResults<T>>({
    data: [],
    query: '',
    canRequestMore: false,
  });
  const isRequestingMoreRef = useRef(false);
  const {buildCancelToken} = useBuildCancelToken();

  const [query, setQuery] = useState('');

  const fetchData = async (searchQuery: string, existingData: ReadonlyArray<T>) => {
    const requestCancelToken = buildCancelToken();
    const gracePeriodCancelTokenSource = buildCancelTokenSource();

    // If there's already data, it's a load more case.
    const isRequestingMore = Boolean(existingData.length);

    if (!isRequestingMore) {
      delayAsync(props.gracePeriod || defaultGracePeriod, gracePeriodCancelTokenSource.token)
        .then(() => {
          setStatus(AsyncStatusesEnum.LOADING);
        })
        .catch(() => {
          /* noop */
        });
    }

    try {
      const newResponse = await props.onRequestData(searchQuery, existingData, requestCancelToken);
      setStatus(AsyncStatusesEnum.LOADED);
      setResponse({
        data: newResponse.data,
        canRequestMore: newResponse.canRequestMore,
        query: searchQuery,
      });
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      setStatus(AsyncStatusesEnum.FAILED);
    } finally {
      gracePeriodCancelTokenSource.cancel();
    }
  };

  useEffect(() => {
    fetchData(query, []);
  }, [query]);

  const fetchMoreData = async () => {
    if (!response.canRequestMore || isRequestingMoreRef.current) {
      return;
    }
    isRequestingMoreRef.current = true;
    await fetchData(response.query, response.data);
    isRequestingMoreRef.current = false;
  };

  const maybeRenderLoadingSpinner = (index: number, items: readonly T[]) => {
    if (!response.canRequestMore || index !== items.length - 1) {
      return null;
    }
    return (
      <StyledLoaderDiv>
        <AsyncLoader
          status={AsyncStatusesEnum.LOADING}
          size={VisualSizesEnum.MEDIUM}
          onRetryClick={fetchMoreData}
        />
      </StyledLoaderDiv>
    );
  };

  return (
    <SearchDropdown
      {...props}
      data={response.data}
      query={query}
      onQueryChange={setQuery}
      onScrollNearBottom={fetchMoreData}
      renderRow={(item, index, items) => (
        <>
          {props.renderRow(item, index, items)}
          {maybeRenderLoadingSpinner(index, items)}
        </>
      )}
      renderOverlay={() => {
        const overlay = props.renderOverlay && props.renderOverlay();
        if (overlay) {
          return overlay;
        }

        if (status !== AsyncStatusesEnum.LOADED) {
          return renderLoading();
        }

        if (status === AsyncStatusesEnum.LOADED && !response.data.length) {
          return renderEmpty();
        }

        return null;
      }}
    />
  );
};
